import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, Users, AlertTriangle, Shield, Heart, User, ExternalLink } from 'lucide-react';
import { AssistedLivingScorecard } from '@/types/scorecard';

interface ScorecardDisplayProps {
  scorecard: AssistedLivingScorecard;
  compact?: boolean;
  showProviderLink?: boolean;
}

const ScorecardDisplay: React.FC<ScorecardDisplayProps> = ({ scorecard, compact = false, showProviderLink = false }) => {
  const renderStars = (rating?: number) => {
    if (!rating) return <span className="text-muted-foreground">No rating</span>;
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= rating 
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-2 text-sm font-medium">{rating}/5</span>
      </div>
    );
  };

  const getRatingColor = (rating?: number) => {
    if (!rating) return 'secondary';
    if (rating >= 4) return 'default';
    if (rating >= 3) return 'secondary';
    return 'destructive';
  };

  if (compact) {
    return (
      <Card className="border-l-4 border-l-primary">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">MN Quality Rating</CardTitle>
            <Badge variant={getRatingColor(scorecard.overall_rating)}>
              {scorecard.overall_rating ? `${scorecard.overall_rating}/5 Stars` : 'No Rating'}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center space-x-1">
              <Heart className="h-3 w-3 text-red-500" />
              <span>Quality: {scorecard.resident_quality_of_life_rating || 'N/A'}/5</span>
            </div>
            <div className="flex items-center space-x-1">
              <Shield className="h-3 w-3 text-green-500" />
              <span>Safety: {scorecard.safety_rating || 'N/A'}/5</span>
            </div>
            <div className="flex items-center space-x-1">
              <Users className="h-3 w-3 text-blue-500" />
              <span>Family: {scorecard.family_satisfaction_rating || 'N/A'}/5</span>
            </div>
            <div className="flex items-center space-x-1">
              <User className="h-3 w-3 text-purple-500" />
              <span>Staff: {scorecard.staffing_rating || 'N/A'}/5</span>
            </div>
          </div>
          {(scorecard.complaints_count || scorecard.violations_count) && (
            <div className="mt-2 pt-2 border-t">
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <AlertTriangle className="h-3 w-3" />
                <span>
                  {scorecard.complaints_count || 0} complaints, {scorecard.violations_count || 0} violations
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Minnesota Quality Scorecard</span>
          <Badge variant={getRatingColor(scorecard.overall_rating)} className="text-sm">
            Overall: {scorecard.overall_rating ? `${scorecard.overall_rating}/5 Stars` : 'No Rating'}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <Heart className="h-4 w-4 text-red-500" />
                <span className="text-sm font-medium">Resident Quality of Life</span>
              </div>
              {renderStars(scorecard.resident_quality_of_life_rating)}
            </div>
            
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <Users className="h-4 w-4 text-blue-500" />
                <span className="text-sm font-medium">Family Satisfaction</span>
              </div>
              {renderStars(scorecard.family_satisfaction_rating)}
            </div>
            
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <Shield className="h-4 w-4 text-green-500" />
                <span className="text-sm font-medium">Safety</span>
              </div>
              {renderStars(scorecard.safety_rating)}
            </div>
          </div>
          
          <div className="space-y-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <Heart className="h-4 w-4 text-purple-500" />
                <span className="text-sm font-medium">Resident Health</span>
              </div>
              {renderStars(scorecard.resident_health_rating)}
            </div>
            
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <User className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium">Staffing</span>
              </div>
              {renderStars(scorecard.staffing_rating)}
            </div>
            
            <div className="pt-2">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span>
                  {scorecard.complaints_count || 0} complaints, {scorecard.violations_count || 0} violations
                </span>
              </div>
            </div>
          </div>
        </div>
        
        {scorecard.inspection_date && (
          <div className="text-xs text-muted-foreground border-t pt-2">
            Last inspection: {new Date(scorecard.inspection_date).toLocaleDateString()}
            {scorecard.total_residents && ` • ${scorecard.total_residents} residents`}
          </div>
        )}
        
        {showProviderLink && (
          <div className="border-t pt-4">
            <button
              onClick={() => window.open('https://www.health.state.mn.us/facilities/regulation/assistedliving/reportcard.html', '_blank')}
              className="flex items-center space-x-2 text-sm text-primary hover:text-primary/80 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              <span>View your facility on Minnesota Assisted Living Report Card</span>
            </button>
            <p className="text-xs text-muted-foreground mt-1">
              Verify that this scorecard data matches your facility's official listing
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ScorecardDisplay;